import { action, makeObservable, observable } from 'mobx'

import { IWorkflowStep, OperationSubjectType } from '~/client/graph'
import OperationRule from '~/client/src/shared/models/OperationRule'
import OperationRulesStore from '~/client/src/shared/stores/domain/OperationRules.store'
import { IWorkflowTypeRuleChanges } from '~/client/src/shared/stores/domain/WorkflowTypes.store'

export type BicRulesSnapshot = ReadonlyMap<string, OperationRule>

const collectRuleIds = (steps: IWorkflowStep[]): Set<string> =>
  new Set((steps || []).flatMap(step => step.workflowRuleIds || []))

/**
 * Unsaved edits to the ball-in-court rules of the workflow type being configured. Rules live
 * apart from the type, so the draft keeps edited copies until the type is saved, and the
 * configurator's undo history records them with each state. Entries are replaced, never
 * mutated, so a snapshot is the map itself.
 */
export default class BicRulesDraft {
  @observable.ref private rules: BicRulesSnapshot = new Map()

  public constructor(
    private readonly operationRulesStore: OperationRulesStore,
  ) {
    makeObservable(this)
  }

  public getRule = (id: string): OperationRule =>
    this.rules.get(id) || this.operationRulesStore.getRuleById(id) || null

  public hasRule = (id: string): boolean => !!this.getRule(id)

  public getPermitBicRules = (ids: string[]): OperationRule[] =>
    (ids || [])
      .map(this.getRule)
      .filter(rule => rule?.operationSubject === OperationSubjectType.PermitBic)

  @action.bound
  public setRule(rule: OperationRule) {
    const rules = new Map(this.rules)
    rules.set(rule.id, rule)
    this.rules = rules
  }

  public snapshot(): BicRulesSnapshot {
    return this.rules
  }

  @action.bound
  public restore(snapshot: BicRulesSnapshot) {
    this.rules = snapshot
  }

  @action.bound
  public clear() {
    this.rules = new Map()
  }

  /**
   * What saving the type writes besides the type: the edited rules its steps still use, and the
   * rules the saved type used that its steps no longer do. Null when there is neither.
   */
  public getChanges(
    steps: IWorkflowStep[],
    savedSteps: IWorkflowStep[],
  ): IWorkflowTypeRuleChanges {
    const usedIds = collectRuleIds(steps)
    const savedRules = Array.from(this.rules.values()).filter(rule =>
      usedIds.has(rule.id),
    )
    const deletedRuleIds = Array.from(collectRuleIds(savedSteps)).filter(
      id => !usedIds.has(id) && this.operationRulesStore.isRuleExisting(id),
    )

    return savedRules.length || deletedRuleIds.length
      ? { savedRules, deletedRuleIds }
      : null
  }

  /** Brings the saved rules in line once the server has written the changes with the type. */
  public applySavedChanges({
    savedRules,
    deletedRuleIds,
  }: IWorkflowTypeRuleChanges) {
    this.operationRulesStore.receiveSavedWithType(savedRules, deletedRuleIds)
  }
}
